import "./ConfirmAction.css";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Button from "../Button/Button";
import {
  closeConfirmationWindow,
  openConfirmationWindow,
} from "../../api/client";

export default function ConfirmAction(props) {
  const [stage, setStage] = useState(0);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const confirmWord = props.confirmWord;
  const matches = !confirmWord || typed === confirmWord;

  function reset() {
    setStage(0);
    setTyped("");
    setError("");
  }

  useEffect(() => {
    if (!stage) return undefined;
    function onKey(e) {
      if (e.key === "Escape" && !busy) reset();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [stage, busy]);

  async function commit() {
    setBusy(true);
    setError("");
    openConfirmationWindow();
    try {
      await props.onConfirm();
      reset();
    } catch (e) {
      setError(e.message || "Something went wrong.");
    } finally {
      closeConfirmationWindow();
      setBusy(false);
    }
  }

  const trigger = (
    <Button
      variant={props.danger ? "secondary" : "primary"}
      small={props.small}
      block={props.block}
      onClick={() => setStage(1)}
      disabled={props.disabled}
    >
      {props.actionLabel}
    </Button>
  );

  if (!stage) return trigger;

  const pane = (
    <div className="confirm-overlay" role="dialog" aria-modal="true">
      <div className={`confirm-pane col${props.danger ? " confirm-pane-danger" : ""}`}>
        <span className="t-eyebrow t-dimmer">
          {stage === 1 ? "Confirm, 1 of 2" : "Confirm, 2 of 2"}
        </span>

        <h2 className="t-h4 confirm-pane-title">
          {stage === 1
            ? props.actionLabel
            : props.confirmLabel || props.actionLabel}
        </h2>

        <p className="t-body-sm confirm-pane-summary">{props.summary}</p>

        {stage === 2 ? (
          <>
            {props.consequence ? (
              <p className="t-body-sm confirm-pane-consequence">
                {props.consequence}
              </p>
            ) : null}
            {confirmWord ? (
              <>
                <p className="t-body-sm t-dimmer">
                  Type{" "}
                  <code className="t-mono confirm-pane-word">{confirmWord}</code>{" "}
                  to continue.
                </p>
                <input
                  className="confirm-pane-input"
                  type="text"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoComplete="off"
                  spellCheck="false"
                  autoFocus
                />
              </>
            ) : null}
          </>
        ) : null}

        <div className="confirm-pane-actions row">
          <Button
            variant="ghost"
            small
            onClick={() => (stage === 2 ? setStage(1) : reset())}
            disabled={busy}
          >
            {stage === 2 ? "Back" : "Cancel"}
          </Button>
          {stage === 1 ? (
            <Button
              variant={props.danger ? "secondary" : "primary"}
              small
              onClick={() => setStage(2)}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant={props.danger ? "secondary" : "primary"}
              small
              busy={busy}
              busyLabel="Working…"
              disabled={!matches}
              onClick={commit}
            >
              {props.confirmLabel || props.actionLabel}
            </Button>
          )}
        </div>

        {error ? (
          <p className="t-body-sm confirm-pane-error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );

  return (
    <>
      {trigger}
      {createPortal(pane, document.body)}
    </>
  );
}
